import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../site/Reveal";
import { SectionLabel, ArrowLink, PlaceholderNote } from "../site/Bits";
import { fetchProjects } from "../../lib/api";

export const WorkCard = ({ project, ratio = "16 / 10", large = false, index = 0 }) => (
  <Reveal variant="image" delay={index * 0.06}>
    <Link
      to={`/work/${project.slug}`}
      data-testid="work-project-card"
      className="group block"
      aria-label={`View project: ${project.title}`}
    >
      <div className="img-frame img-zoom" style={{ aspectRatio: ratio }}>
        <img src={project.image} alt={project.title} loading="lazy" />
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-6">
        <span className="project-meta" style={{ color: "var(--muted-ink-2)" }}>
          {project.category}
        </span>
        <span className="text-[12px]" style={{ color: "rgba(23,21,34,0.45)" }}>
          {project.year}
        </span>
      </div>

      <h3
        className={`project-title mt-3 ${large ? "" : "text-[clamp(1.25rem,1.7vw,1.6rem)]"}`}
      >
        {project.title}
      </h3>

      {project.summary && (
        <p className="mt-3 max-w-[52ch] text-[14px] leading-[1.6] body-muted">{project.summary}</p>
      )}

      <span
        className="mt-5 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase"
        style={{ letterSpacing: "0.16em", color: "#352A59" }}
      >
        View Project
        <span
          aria-hidden="true"
          className="inline-block transition-transform duration-300 group-hover:translate-x-1"
        >
          &rarr;
        </span>
      </span>
    </Link>
  </Reveal>
);

export default function FeaturedWork() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchProjects()
      .then((data) => {
        if (!alive) return;
        const featured = data.filter((p) => p.featured);
        setProjects((featured.length ? featured : data).slice(0, 5));
      })
      .catch(() => setProjects([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const [first, second, third, fourth, fifth] = projects;

  return (
    <section className="lk-section" data-testid="featured-work-section" id="featured-work">
      <div className="lk-container-wide">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <SectionLabel>Featured Work</SectionLabel>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-lg mt-5 max-w-[22ch]">IDEAS WE HAVE BROUGHT TO LIFE.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.14}>
            <ArrowLink to="/work" data-testid="featured-work-view-all">
              View All Work
            </ArrowLink>
          </Reveal>
        </div>

        {loading && (
          <div className="mt-12 grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="w-full animate-pulse" style={{ aspectRatio: "16 / 11", background: "var(--lykit-lavender)" }} />
            </div>
            <div className="lg:col-span-5 space-y-8">
              <div className="w-full animate-pulse" style={{ aspectRatio: "16 / 10", background: "var(--lykit-lavender)" }} />
              <div className="w-full animate-pulse" style={{ aspectRatio: "16 / 10", background: "var(--lykit-lavender)" }} />
            </div>
          </div>
        )}

        {!loading && projects.length === 0 && (
          <div className="mt-12">
            <p className="lead measure">
              No projects have been published yet. Project entries will appear here once the LYKIT team adds them.
            </p>
            <div className="mt-8">
              <ArrowLink to="/contact">Start a conversation</ArrowLink>
            </div>
          </div>
        )}

        {!loading && projects.length > 0 && (
          <>
            <div className="mt-[clamp(32px,3.4vw,56px)] grid gap-x-14 gap-y-12 lg:grid-cols-12">
              {first && (
                <div className="lg:col-span-7">
                  <WorkCard project={first} ratio="16 / 11" large index={0} />
                </div>
              )}
              <div className="lg:col-span-5 lg:pt-[clamp(0px,2.4vw,40px)] space-y-10">
                {second && <WorkCard project={second} ratio="16 / 10" index={1} />}
                {third && <WorkCard project={third} ratio="16 / 10" index={2} />}
              </div>
            </div>

            <div className="mt-12 grid gap-x-14 gap-y-12 lg:grid-cols-12">
              {fourth && (
                <div className="lg:col-span-5">
                  <WorkCard project={fourth} ratio="5 / 4" index={0} />
                </div>
              )}
              {fifth && (
                <div className="lg:col-span-6 lg:col-start-7 lg:pt-[clamp(0px,3vw,52px)]">
                  <WorkCard project={fifth} ratio="16 / 10" index={1} />
                </div>
              )}
            </div>

            <div className="mt-10">
              <PlaceholderNote>
                Project entries are structured placeholders — real case studies are added by the LYKIT team.
              </PlaceholderNote>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
